// REQ-P1-06b: 会場一覧の契約型付きQueryフック。
// 一覧画面の新規GETは生fetchではなく、この層を経由してQueryキャッシュを正本にする。

import { useQuery } from '@tanstack/react-query';
import { api, type paths } from './client';
import type { operations } from './types.gen';
import { ApiStatusError } from './templates';

type VenueListQuery = NonNullable<paths['/venuelist']['get']['parameters']['query']>;
type VenueListResponse = NonNullable<
  paths['/venuelist']['get']['responses'][200]['content']
>['application/json'];
type VenueGenreListResponse = NonNullable<
  paths['/venue-genres']['get']['responses'][200]['content']
>['application/json'];
type VenueDetailResponse = NonNullable<
  paths['/venues/{venueId}']['get']['responses'][200]['content']
>['application/json'];
type VenueCreateRequest = NonNullable<
  paths['/venues']['post']['requestBody']
>['content']['application/json'];
type VenueCreateResponse = NonNullable<
  paths['/venues']['post']['responses'][201]['content']
>['application/json'];
type VenueMutationRequest = NonNullable<
  paths['/venues/{venueId}']['put']['requestBody']
>['content']['application/json'];
type VenueNameAdvancedQuery = NonNullable<
  paths['/venues/check-name-advanced']['get']['parameters']['query']
>;
type VenueNameAdvancedResponse = NonNullable<
  paths['/venues/check-name-advanced']['get']['responses'][200]['content']
>['application/json'];
type VenuePricingsResponse = NonNullable<
  paths['/venues/{venueId}/pricings']['get']['responses'][200]['content']
>['application/json'];
type VenuePricingsMutationRequest = NonNullable<
  paths['/venues/{venueId}/pricings']['put']['requestBody']
>['content']['application/json'];
type VenuePastEventsResponse = NonNullable<
  paths['/venues/{venueId}/past-events']['get']['responses'][200]['content']
>['application/json'];
type VenueMaterialsQuery = NonNullable<
  operations['listVenueMaterials']['parameters']['query']
>;
type VenueMaterialsResponse = NonNullable<
  operations['listVenueMaterials']['responses'][200]['content']
>['application/json'];
type VenueMaterialMutationRequest = NonNullable<
  paths['/venues/{venueId}/materials']['post']['requestBody']
>['content']['application/json'];
type VenueMaterialPatchRequest = NonNullable<
  paths['/venues/{venueId}/materials/{materialId}']['put']['requestBody']
>['content']['application/json'];
type MaterialPresignPutRequest = NonNullable<
  paths['/storage/materials/presign-put']['post']['requestBody']
>['content']['application/json'];
type MaterialPresignPutResponse = NonNullable<
  paths['/storage/materials/presign-put']['post']['responses'][200]['content']
>['application/json'];
type StoragePresignGetQuery = NonNullable<
  operations['createStoragePresignGet']['parameters']['query']
>;
type StoragePresignGetResponse = NonNullable<
  operations['createStoragePresignGet']['responses'][200]['content']
>['application/json'];
type StorageProxyUrlQuery = NonNullable<
  paths['/storage/proxy-url']['get']['parameters']['query']
>;
type StorageProxyUrlResponse = NonNullable<
  paths['/storage/proxy-url']['get']['responses'][200]['content']
>['application/json'];

export type VenueRow = VenueListResponse['venues'][number];
export type VenueGenreRow = VenueGenreListResponse['genres'][number];

export async function listVenues(query: VenueListQuery): Promise<VenueListResponse> {
  const { data, response } = await api.GET('/venuelist', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const venues = Array.isArray(data.venues) ? data.venues : [];
  const total = Number(data.total);
  const page = Number(data.page);
  const limit = Number(data.limit);
  return {
    venues,
    total: Number.isFinite(total) ? total : venues.length,
    page: Number.isFinite(page) ? page : query.page ?? 1,
    limit: Number.isFinite(limit) ? limit : query.limit ?? venues.length,
  };
}

export async function listVenueGenres(): Promise<VenueGenreRow[]> {
  const { data, response } = await api.GET('/venue-genres');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data.genres) ? data.genres : [];
}

export async function checkVenueNameAdvanced(
  query: VenueNameAdvancedQuery
): Promise<VenueNameAdvancedResponse> {
  const { data, response } = await api.GET('/venues/check-name-advanced', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as VenueNameAdvancedResponse;
}

export async function createVenue(body: VenueCreateRequest): Promise<Record<string, unknown>> {
  const { data, response } = await api.POST('/venues', { body });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const venue = (data as VenueCreateResponse).venue;
  return venue && typeof venue === 'object' ? (venue as Record<string, unknown>) : {};
}

export async function getVenueDetail(venueId: string): Promise<Record<string, unknown>> {
  const { data, response } = await api.GET('/venues/{venueId}', {
    params: { path: { venueId } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const venue = (data as VenueDetailResponse).venue;
  return venue && typeof venue === 'object' ? (venue as Record<string, unknown>) : {};
}

export async function updateVenueDetail(
  venueId: string,
  body: VenueMutationRequest
): Promise<Record<string, unknown>> {
  const { data, response } = await api.PUT('/venues/{venueId}', {
    params: { path: { venueId } },
    body,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const venue = (data as VenueDetailResponse).venue;
  return venue && typeof venue === 'object' ? (venue as Record<string, unknown>) : {};
}

export async function deleteVenueById(venueId: string): Promise<void> {
  const { response } = await api.DELETE('/venues/{venueId}', {
    params: { path: { venueId } },
  });
  if (!response.ok) throw new ApiStatusError(response.status);
}

export async function listVenuePricings(venueId: string): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/venues/{venueId}/pricings', {
    params: { path: { venueId } },
  });
  if (response.status === 404) return [];
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const pricings = (data as VenuePricingsResponse).pricings;
  return Array.isArray(pricings) ? pricings : [];
}

export async function replaceVenuePricings(
  venueId: string,
  body: VenuePricingsMutationRequest
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.PUT('/venues/{venueId}/pricings', {
    params: { path: { venueId } },
    body,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const pricings = (data as VenuePricingsResponse).pricings;
  return Array.isArray(pricings) ? pricings : [];
}

export async function listVenuePastEvents(venueId: string): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/venues/{venueId}/past-events', {
    params: { path: { venueId } },
  });
  if (response.status === 404) return [];
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const events = (data as VenuePastEventsResponse).events;
  return Array.isArray(events) ? events : [];
}

export async function listVenueMaterials(
  venueId: string,
  query: VenueMaterialsQuery = {}
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/venues/{venueId}/materials', {
    params: { path: { venueId }, query },
  });
  if (response.status === 404) return [];
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const materials = (data as VenueMaterialsResponse).materials;
  return Array.isArray(materials) ? materials : [];
}

export async function createVenueMaterial(
  venueId: string,
  body: VenueMaterialMutationRequest
): Promise<Record<string, unknown>> {
  const { data, response } = await api.POST('/venues/{venueId}/materials', {
    params: { path: { venueId } },
    body,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const material = (data as { material?: unknown }).material;
  return material && typeof material === 'object' ? (material as Record<string, unknown>) : {};
}

export async function updateVenueMaterial(
  venueId: string,
  materialId: number | string,
  body: VenueMaterialPatchRequest
): Promise<Record<string, unknown>> {
  const { data, response } = await api.PUT('/venues/{venueId}/materials/{materialId}', {
    params: { path: { venueId, materialId } },
    body,
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const material = (data as { material?: unknown }).material;
  return material && typeof material === 'object' ? (material as Record<string, unknown>) : {};
}

export async function deleteVenueMaterial(
  venueId: string,
  materialId: number | string
): Promise<void> {
  const { response } = await api.DELETE('/venues/{venueId}/materials/{materialId}', {
    params: { path: { venueId, materialId } },
  });
  if (!response.ok) throw new ApiStatusError(response.status);
}

export async function createMaterialPresignPut(
  body: MaterialPresignPutRequest
): Promise<MaterialPresignPutResponse> {
  const { data, response } = await api.POST('/storage/materials/presign-put', { body });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as MaterialPresignPutResponse;
}

export async function createStoragePresignGet(
  query: StoragePresignGetQuery
): Promise<StoragePresignGetResponse> {
  const { data, response } = await api.GET('/storage/presign-get', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as StoragePresignGetResponse;
}

export async function createStorageProxyUrl(
  query: StorageProxyUrlQuery
): Promise<StorageProxyUrlResponse> {
  const { data, response } = await api.GET('/storage/proxy-url', {
    params: { query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return data as StorageProxyUrlResponse;
}

export function uploadMaterialToPresignedUrl(
  url: string,
  file: File,
  headers: Record<string, unknown> = {}
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', url);
    Object.entries(headers || {}).forEach(([key, value]) => {
      if (value != null) request.setRequestHeader(key, String(value));
    });
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload failed: HTTP ${request.status}`));
    };
    request.onerror = () => reject(new Error('Upload failed'));
    request.send(file);
  });
}

export function lookupPostalCodeAddress(
  postalCode: string
): Promise<{ prefecture: string; city: string; address: string } | null> {
  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open(
      'GET',
      `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${encodeURIComponent(postalCode)}`
    );
    request.onload = () => {
      if (request.status < 200 || request.status >= 300) {
        resolve(null);
        return;
      }
      try {
        const payload = JSON.parse(request.responseText || '{}');
        const result = Array.isArray(payload?.results) ? payload.results[0] : null;
        if (!result) {
          resolve(null);
          return;
        }
        resolve({
          prefecture: String(result.address1 || ''),
          city: String(result.address2 || ''),
          address: String(result.address3 || ''),
        });
      } catch {
        resolve(null);
      }
    };
    request.onerror = () => resolve(null);
    request.send();
  });
}

export function useVenuesQuery(query: VenueListQuery, enabled: boolean) {
  return useQuery({
    queryKey: ['venues', query],
    queryFn: () => listVenues(query),
    enabled,
  });
}

export function useVenueGenresQuery() {
  return useQuery({
    queryKey: ['venue-genres'],
    queryFn: listVenueGenres,
  });
}
